import type {
  AdminPickerRecentReportDto,
  AdminPickerReportStatus,
  AdminPickerSource,
} from '@carcheck/shared';
import Chip, { type ChipProps } from '@mui/material/Chip';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Badge chips for the pickers list + drawer (AUT-400), colours after the
 * mockup's `badge--{success,danger,info,neutral}`. Labels via i18n.
 */

type ChipColor = ChipProps['color'];

export const PickerStateBadge = ({
  disabled,
  testId,
}: {
  disabled: boolean;
  testId?: string;
}): ReactElement => {
  const { t } = useTranslation();
  return (
    <Chip
      data-testid={testId}
      size="small"
      color={disabled ? 'error' : 'success'}
      label={t(disabled ? 'pickers.state.disabled' : 'pickers.state.active')}
    />
  );
};

const SOURCE_COLOR: Record<AdminPickerSource, ChipColor> = {
  invite: 'info',
  self_signup: 'default',
  org: 'primary',
};

export const PickerSourceBadge = ({
  source,
  testId,
}: {
  source: AdminPickerSource;
  testId?: string;
}): ReactElement => {
  const { t } = useTranslation();
  return (
    <Chip
      data-testid={testId}
      size="small"
      variant="outlined"
      color={SOURCE_COLOR[source]}
      label={t(`pickers.source.${source}`)}
    />
  );
};

const REPORT_STATUS_COLOR: Record<AdminPickerReportStatus, ChipColor> = {
  final: 'success',
  draft: 'default',
};

export const ReportStatusBadge = ({
  status,
}: {
  status: AdminPickerReportStatus;
}): ReactElement => {
  const { t } = useTranslation();
  return (
    <Chip
      size="small"
      color={REPORT_STATUS_COLOR[status]}
      label={t(`pickers.detail.reports.status.${status}`)}
    />
  );
};

/** active → green; revoked → «отозвана · сам / админ»; none → «нет». */
export const LinkStateBadge = ({
  link,
}: {
  link: AdminPickerRecentReportDto['link'];
}): ReactElement => {
  const { t } = useTranslation();
  if (link.state === 'active') {
    return <Chip size="small" color="success" label={t('pickers.detail.reports.link.active')} />;
  }
  if (link.state === 'revoked') {
    const by = link.revokedBy ?? 'picker';
    return (
      <Chip
        size="small"
        color={by === 'admin' ? 'error' : 'default'}
        label={t(`pickers.detail.reports.link.revoked.${by}`)}
      />
    );
  }
  return <Chip size="small" variant="outlined" label={t('pickers.detail.reports.link.none')} />;
};
